/**
 * Hook de surveillance de session Supabase
 * Détecte les sessions expirées et redirige vers la page de login
 */
import { useEffect, useRef } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { supabase } from '@/integrations/supabase/client';
import { toast } from '@/hooks/use-toast';

/**
 * Surveille l'état de la session Supabase et gère les expirations
 *
 * Fonctionnalités :
 * - Détecte quand la session expire (SIGNED_OUT, TOKEN_REFRESHED échoué)
 * - Affiche un toast d'avertissement
 * - Sauvegarde l'URL actuelle pour y revenir après reconnexion
 * - Redirige vers /auth après un délai de 2 secondes
 */
export function useSessionMonitor() {
  const navigate = useNavigate();
  const location = useLocation();

  // Éviter les redirections multiples
  const isRedirecting = useRef(false);

  useEffect(() => {
    const { data: { subscription } } = supabase.auth.onAuthStateChange((event, session) => {
      // Session expirée ou refresh token échoué
      const isSessionExpired =
        event === 'SIGNED_OUT' ||
        (event === 'TOKEN_REFRESHED' && !session);

      // Ne pas traiter si on est déjà sur la page d'auth ou si redirection en cours
      const isOnAuthPage = location.pathname === '/auth' || location.pathname === '/login';

      if (isSessionExpired && !isOnAuthPage && !isRedirecting.current) {
        isRedirecting.current = true;

        // Afficher le toast d'avertissement
        toast({
          title: 'Session expirée',
          description: 'Veuillez vous reconnecter pour continuer.',
          variant: 'destructive',
        });

        // Sauvegarder l'URL actuelle pour redirect après login
        // Ne pas sauvegarder les pages publiques ou d'auth
        const publicPaths = ['/', '/auth', '/login', '/pricing', '/product', '/blog', '/contact'];
        const isPublicPath = publicPaths.some(path =>
          location.pathname === path || location.pathname.startsWith('/product/') || location.pathname.startsWith('/blog/')
        );

        if (!isPublicPath) {
          const returnUrl = location.pathname + location.search;
          sessionStorage.setItem('returnUrl', returnUrl);
        }

        // Rediriger vers auth après un court délai (pour que l'utilisateur voie le toast)
        setTimeout(() => {
          navigate('/auth', { replace: true });
          isRedirecting.current = false;
        }, 2000);
      }

      // Reset du flag si l'utilisateur se reconnecte
      if (event === 'SIGNED_IN') {
        isRedirecting.current = false;
      }
    });

    return () => {
      subscription.unsubscribe();
    };
  }, [navigate, location]);
}

/**
 * Récupère l'URL de retour après reconnexion et la supprime du storage
 * À utiliser dans la page Auth après un login réussi
 */
export function getAndClearReturnUrl(): string | null {
  const returnUrl = sessionStorage.getItem('returnUrl');
  if (returnUrl) {
    sessionStorage.removeItem('returnUrl');
  }
  return returnUrl;
}
